import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ActivateEvidenceDocument,
  type ActivateEvidenceMutation,
  type ActivateEvidenceMutationVariables,
  DeactivateEvidenceDocument,
  type DeactivateEvidenceMutation,
  type DeactivateEvidenceMutationVariables,
} from "~/features/evidences/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/**
 * Activate / deactivate an evidence via the direct `cnut(De)activateEvidence`
 * mutations. Returns plain submitters + a `processing` flag; the calling
 * container owns the confirm dialog.
 */
export function useEvidenceActivation() {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [activate] = useMutation<ActivateEvidenceMutation, ActivateEvidenceMutationVariables>(ActivateEvidenceDocument);
  const [deactivate] = useMutation<DeactivateEvidenceMutation, DeactivateEvidenceMutationVariables>(
    DeactivateEvidenceDocument,
  );

  const run = useCallback(
    async (id: string, shouldDeactivate: boolean) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      setProcessing(true);
      try {
        if (shouldDeactivate) {
          await deactivate({ variables: { input: { tenantId, id } } });
        } else {
          await activate({ variables: { input: { tenantId, id } } });
        }
        toast.success(
          shouldDeactivate
            ? t("feature.common.deactivatedSuccessfully", { model: "evidence" })
            : t("feature.common.activatedSuccessfully", { model: "evidence" }),
        );
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [activate, deactivate, tenantId, t],
  );

  const onActivate = useCallback((id: string) => run(id, false), [run]);
  const onDeactivate = useCallback((id: string) => run(id, true), [run]);

  return { onActivate, onDeactivate, processing };
}
